import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { StockLocation } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteLocation } from '../hooks/use-location-mutations';
import { locationDeleteErrorKey } from '../schemas/inventory-rules';

export interface DeleteLocationDialogProps {
  storeId: string;
  location: StockLocation | null;
  onOpenChange: (open: boolean) => void;
}

/** Stays open until the backend answered; a 409 says whether stock or the default flag is in the way. */
export function DeleteLocationDialog({
  storeId,
  location,
  onOpenChange,
}: DeleteLocationDialogProps) {
  const { t } = useTranslation(['inventory', 'common', 'errors']);
  const message = useErrorMessage();
  const remove = useDeleteLocation(storeId);
  const errorKey = remove.error ? locationDeleteErrorKey(remove.error) : null;
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={location !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('inventory:locations.deleteDialog.title', { name: location?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('inventory:locations.deleteDialog.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {errorKey ? t(errorKey) : message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              event.preventDefault();
              if (location) remove.mutate(location.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending
              ? t('inventory:locations.deleteDialog.submitting')
              : t('inventory:locations.deleteDialog.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
